// DAS-2107: "Your News" modal for PortfolioAnalysisView
// Shows the latest Trendsearch article per portfolio ticker.
// Fetches on mount via pa_view dispatcher (topic: "news").

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, Link,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchNews } from '../../helpers/portfolioAnalysisHelper';
import ClickableTickerChip from './ClickableTickerChip';

const impactColor = (type) => {
  if (!type) return { bg: '#e0e0e0', fg: '#666' };
  const t = String(type).toLowerCase();
  if (t.includes('winner'))  return { bg: '#e8f5e9', fg: '#2e7d32', border: '#66bb6a' };
  if (t.includes('loser'))   return { bg: '#ffebee', fg: '#c62828', border: '#ef5350' };
  if (t.includes('enabler')) return { bg: '#e3f2fd', fg: '#1565c0', border: '#42a5f5' };
  if (t.includes('mixed'))   return { bg: '#fff3e0', fg: '#e65100', border: '#ffa726' };
  if (t.includes('neutral')) return { bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' };
  return { bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' };
};

const isRecent = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  const diffMs = now - d;
  return diffMs >= 0 && diffMs < 2 * 24 * 60 * 60 * 1000;
};

const PAModal_News = ({ tickers, data, source = 'portfolio' }) => {
  const [newsData, setNewsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current || !tickers || tickers.length === 0) return;
    fetchedRef.current = true;

    setLoading(true);
    fetchNews(tickers)
      .then(result => setNewsData(result.news || {}))
      .catch(err => console.error('[PAModal_News] Failed to fetch news:', err))
      .finally(() => setLoading(false));
  }, [tickers]);

  const rows = useMemo(() => {
    return Object.entries(newsData).map(([ticker, n]) => {
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());
      return {
        ticker,
        company: position?.Company || n.company_name || '',
        date: n.date || '',
        articleTitle: n.article_title || '',
        articleUrl: n.article_url || '',
        impactType: n.impact_type || '',
        description: n.description || '',
        sourceName: n.source_name || '',
        sentimentScore: n.sentiment_score,
      };
    });
  }, [newsData, data]);

  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const lower = search.toLowerCase();
      list = list.filter(r =>
        r.ticker.toLowerCase().includes(lower) ||
        r.company.toLowerCase().includes(lower) ||
        r.articleTitle.toLowerCase().includes(lower) ||
        r.sourceName.toLowerCase().includes(lower)
      );
    }
    return [...list].sort((a, b) => {
      let av, bv;
      if (orderBy === 'date') {
        av = a.date || '';
        bv = b.date || '';
      } else if (orderBy === 'ticker') {
        av = a.ticker;
        bv = b.ticker;
      } else if (orderBy === 'sentimentScore') {
        av = a.sentimentScore ?? -Infinity;
        bv = b.sentimentScore ?? -Infinity;
      } else {
        av = a[orderBy] || '';
        bv = b[orderBy] || '';
      }
      if (av < bv) return order === 'asc' ? -1 : 1;
      if (av > bv) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, search, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder(field === 'date' ? 'desc' : 'asc');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ ml: 2 }}>Loading news…</Typography>
      </Box>
    );
  }

  const tickersWithNews = Object.keys(newsData).length;
  const tickersWithoutNews = (tickers?.length || 0) - tickersWithNews;

  if (rows.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No Trendsearch articles found for portfolio tickers</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          placeholder="Search ticker, company, title, source..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {tickersWithNews} ticker{tickersWithNews !== 1 ? 's' : ''} with news
          {tickersWithoutNews > 0 && ` · ${tickersWithoutNews} without`}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                <TableSortLabel active={orderBy === 'ticker'} direction={orderBy === 'ticker' ? order : 'asc'} onClick={() => handleSort('ticker')}>
                  Ticker
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 90 }}>
                <TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'desc'} onClick={() => handleSort('date')}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 80 }} align="center">
                <TableSortLabel active={orderBy === 'impactType'} direction={orderBy === 'impactType' ? order : 'asc'} onClick={() => handleSort('impactType')}>
                  Impact
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                <TableSortLabel active={orderBy === 'sourceName'} direction={orderBy === 'sourceName' ? order : 'asc'} onClick={() => handleSort('sourceName')}>
                  Source
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => {
              const ic = impactColor(row.impactType);
              const recent = isRecent(row.date);
              return (
                <TableRow
                  key={row.ticker}
                  hover
                  sx={recent ? {
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                    borderLeft: '3px solid #1976d2',
                  } : undefined}
                >
                  <TableCell>
                    <ClickableTickerChip ticker={row.ticker} source={source} />
                    {row.company && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {row.company}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {row.date ? new Date(row.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      {recent && (
                        <Chip label="NEW" size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.articleUrl ? (
                      <Link
                        href={row.articleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}
                      >
                        {row.articleTitle || 'View article'}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{row.articleTitle || '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.impactType ? (
                      <Chip
                        label={row.impactType}
                        size="small"
                        sx={{
                          bgcolor: ic.bg,
                          color: ic.fg,
                          border: `1px solid ${ic.border}`,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                        }}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {row.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.sourceName || '—'}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

export default PAModal_News;
